import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import Navbar from "./Navbar";
import { auth, db } from "./firebase";
import { collection, getDocs } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import Username from "./components/Username";
import { isProUser } from "./utils/entitlements";

function PublicHome() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [featuredBuilds, setFeaturedBuilds] = useState([]);

  useEffect(() => {
  const unsubscribe = onAuthStateChanged(auth, (user) => {
    setIsLoggedIn(!!user);
  });

  return () => unsubscribe();
}, []);


  useEffect(() => {
    const loadFeaturedBuilds = async () => {
      try {
        const snapshot = await getDocs(collection(db, "profiles"));
        const builds = [];

        snapshot.forEach((docItem) => {
          const profile = docItem.data();
          const username = profile.username || docItem.id;
          const cars = profile.garageCars || [];

          cars.forEach((car) => {
            const image = car.images?.[0] || null;
            const title = `${car.year || ""} ${car.make || ""} ${car.model || ""}`.trim();

            const isEligible =
              image &&
              car.year &&
              car.make &&
              car.model &&
              car.id &&
              username;

            if (isEligible) {
              builds.push({
                id: car.id,
                username,
                title,
                image,
                ownerIsPro: isProUser(profile)
              });
            }
          });
        });

        const dailyFeatured = getDailyFeaturedBuilds(builds, 3);
        setFeaturedBuilds(dailyFeatured);
      } catch (error) {
        console.log("Error loading featured builds:", error);
      }
    };

    loadFeaturedBuilds();
  }, []);

  return (
    <>
      <Navbar />

      <main style={pageStyle}>
        <section style={heroStyle}>
          <div style={heroContentStyle}>
            <p style={eyebrowStyle}>Built for automotive enthusiasts</p>

            <h1 style={headlineStyle}>
              Your build deserves more than social media.
            </h1>

            <p style={subTextStyle}>
              Every modification tells a story. MyBuildID gives your vehicle a
              permanent home to document the journey, share the details, and
              inspire the next enthusiast.
            </p>

            <div style={buttonRowStyle}>
<Link
  to={isLoggedIn ? "/setup" : "/auth"}
  style={primaryButtonStyle}
>
  Access or Create Your Garage
</Link>
            </div>
          </div>
        </section>

        <section style={featuredSectionStyle}>
          <div style={sectionHeaderStyle}>
            <h2 style={sectionTitleStyle}>
              See what the community is building.
            </h2>

            <p style={sectionSubtitleStyle}>
              Browse documented builds from enthusiasts and discover ideas for
              your next project.
            </p>
          </div>

          <div style={featuredGridStyle}>
            {featuredBuilds.length > 0 ? (
              featuredBuilds.map((build) => (
                <Link
                  key={`${build.username}-${build.id}`}
                  to={`/user/${build.username}/car/${build.id}`}
                  style={featuredCardStyle}
                >
                  <img
                    src={build.image}
                    alt={build.title}
                    style={featuredImageStyle}
                  />

                  <div style={featuredCardBodyStyle}>
                    <h3 style={featuredCarTitleStyle}>
                      {build.title || "Featured Build"}
                    </h3>
                    <p style={featuredUserStyle}><Username username={build.username} isPro={build.ownerIsPro} /></p>
                  </div>
                </Link>
              ))
            ) : (
              <div style={featuredCardStyle}>
                <div style={featuredImagePlaceholderStyle}></div>
                <div style={featuredCardBodyStyle}>
                  <h3 style={featuredCarTitleStyle}>
                    Featured builds coming soon
                  </h3>
                  <p style={featuredUserStyle}>Be one of the first.</p>
                </div>
              </div>
            )}
          </div>

          <Link to="/discover" style={secondaryButtonStyle}>
            Browse All Builds
          </Link>
        </section>

        <section style={closingSectionStyle}>
          <h2 style={sectionTitleStyle}>Built to preserve the journey.</h2>

          <p style={sectionSubtitleStyle}>
            From first mod to final setup, MyBuildID keeps your build history
            organized, searchable, and easy to share.
          </p>
        </section>
      </main>
    </>
  );
}

function getDailyFeaturedBuilds(builds, count) {
  if (!builds.length) {
    return [];
  }

  const today = new Date().toISOString().split("T")[0];
  const seed = getSeedFromString(today);
  const shuffled = seededShuffle(builds, seed);

  return shuffled.slice(0, count);
}

function getSeedFromString(text) {
  let seed = 0;

  for (let i = 0; i < text.length; i += 1) {
    seed = (seed * 31 + text.charCodeAt(i)) >>> 0;
  }

  return seed;
}

function seededShuffle(array, seed) {
  const shuffled = [...array];
  let currentSeed = seed;

  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    currentSeed = (currentSeed * 1664525 + 1013904223) >>> 0;

    const randomIndex = currentSeed % (i + 1);
    const temp = shuffled[i];

    shuffled[i] = shuffled[randomIndex];
    shuffled[randomIndex] = temp;
  }

  return shuffled;
}

const pageStyle = {
  backgroundColor: "#111",
  color: "white",
  minHeight: "100vh",
  fontFamily: "Arial",
  padding: "calc(86px + env(safe-area-inset-top)) 16px 80px"
};

const heroStyle = {
  maxWidth: "980px",
  margin: "0 auto 30px",
  textAlign: "center"
};

const heroContentStyle = {
  maxWidth: "760px",
  margin: "0 auto"
};

const eyebrowStyle = {
  color: "#e10600",
  fontWeight: "bold",
  textTransform: "uppercase",
  letterSpacing: "1px",
  fontSize: "12px",
  marginBottom: "10px"
};

const headlineStyle = {
  fontSize: "clamp(38px, 7vw, 72px)",
  lineHeight: "0.95",
  margin: "0 0 18px"
};

const subTextStyle = {
  opacity: 0.85,
  lineHeight: "1.6",
  fontSize: "17px",
  maxWidth: "650px",
  margin: "0 auto 24px"
};

const buttonRowStyle = {
  display: "flex",
  justifyContent: "center"
};

const primaryButtonStyle = {
  backgroundColor: "#e10600",
  color: "white",
  padding: "14px 20px",
  borderRadius: "12px",
  textDecoration: "none",
  fontWeight: "bold",
  display: "inline-block"
};

const secondaryButtonStyle = {
  ...primaryButtonStyle,
  backgroundColor: "#222",
  border: "1px solid #333",
  marginTop: "8px"
};

const featuredSectionStyle = {
  maxWidth: "1100px",
  margin: "0 auto 42px",
  textAlign: "center"
};

const sectionHeaderStyle = {
  marginBottom: "20px"
};

const sectionTitleStyle = {
  fontSize: "26px",
  margin: "0 0 8px",
  lineHeight: "1.15"
};

const sectionSubtitleStyle = {
  opacity: 0.76,
  margin: "0 auto",
  lineHeight: "1.55",
  fontSize: "16px",
  maxWidth: "620px"
};

const featuredGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))",
  gap: "16px",
  marginBottom: "20px"
};

const featuredCardStyle = {
  background: "#181818",
  borderRadius: "18px",
  overflow: "hidden",
  border: "1px solid #2b2b2b",
  textAlign: "left",
  textDecoration: "none",
  color: "white",
  display: "block"
};

const featuredImageStyle = {
  width: "100%",
  height: "160px",
  objectFit: "cover",
  display: "block"
};

const featuredImagePlaceholderStyle = {
  height: "160px",
  background: "linear-gradient(135deg, #2a2a2a, #111)"
};

const featuredCardBodyStyle = {
  padding: "16px"
};

const featuredCarTitleStyle = {
  margin: "0 0 6px",
  fontSize: "18px"
};

const featuredUserStyle = {
  margin: 0,
  opacity: 0.65
};

const closingSectionStyle = {
  maxWidth: "760px",
  margin: "42px auto 0",
  textAlign: "center",
  padding: "28px 16px",
  borderTop: "1px solid #242424"
};

export default PublicHome;
